/**
 * PhotoLightbox - Centralized lightbox for viewing full-size photos
 * 
 * Single source of truth for:
 * - Full-size image viewing
 * - Caption editing
 * - Visibility editing
 * - Closing
 * 
 * NEVER opens in new tab. ALWAYS modal overlay.
 */

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { X, Eye, EyeSlash, PencilSimple, FloppyDisk, SpinnerGap, Image as ImageIcon } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Build proper URL
const buildUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

export function PhotoLightbox({
  open,
  onOpenChange,
  photo,
  canEdit = false,
  onUpdate,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editCaption, setEditCaption] = useState("");
  const [editVisibility, setEditVisibility] = useState("internal");
  const [saving, setSaving] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Reset state when photo changes
  useEffect(() => {
    if (photo) {
      setEditCaption(photo.caption || "");
      setEditVisibility(photo.visibility || "internal");
      setIsEditing(false);
      setImageLoaded(false);
      setImageError(false);
    }
  }, [photo]);

  const handleSave = async () => {
    if (!photo?.photo_id) return;
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("caption", editCaption);
      formData.append("visibility", editVisibility);

      const response = await fetch(`${API}/photos/${photo.photo_id}`, {
        method: "PUT",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        toast.success("Photo updated");
        onUpdate?.();
        setIsEditing(false);
      } else {
        toast.error("Failed to update photo");
      }
    } catch (error) {
      toast.error("Failed to update photo");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onOpenChange(false);
  };

  const fullSrc = buildUrl(photo?.file_url);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden bg-black/95 border-none">
        {/* Visually hidden title for accessibility */}
        <span className="sr-only">Photo viewer</span>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
          aria-label="Close"
          data-testid="lightbox-close-btn"
        >
          <X size={24} weight="bold" />
        </button>

        {/* Main image area */}
        <div className="flex items-center justify-center min-h-[400px] max-h-[70vh] bg-black/95">
          {!imageLoaded && !imageError && (
            <div className="flex flex-col items-center gap-2 text-white/50">
              <SpinnerGap size={32} className="animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
          {imageError && (
            <div className="flex flex-col items-center gap-2 text-white/50">
              <ImageIcon size={48} />
              <span className="text-sm">Failed to load image</span>
            </div>
          )}
          <img
            src={fullSrc}
            alt={photo?.caption || "Photo"}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            className={`max-w-full max-h-[70vh] object-contain transition-opacity ${
              imageLoaded && !imageError ? 'opacity-100' : 'opacity-0 absolute'
            }`}
          />
        </div>

        {/* Bottom panel - caption/visibility editing */}
        <div className="bg-zinc-900 p-4 border-t border-zinc-700">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-white text-sm">Caption</Label>
                <Input
                  value={editCaption}
                  onChange={(e) => setEditCaption(e.target.value)}
                  className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                  placeholder="Add a caption..."
                />
              </div>
              <div>
                <Label className="text-white text-sm">Visibility</Label>
                <Select value={editVisibility} onValueChange={setEditVisibility}>
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">
                      <div className="flex items-center gap-2">
                        <EyeSlash size={16} />
                        Internal Only
                      </div>
                    </SelectItem>
                    <SelectItem value="customer">
                      <div className="flex items-center gap-2">
                        <Eye size={16} />
                        Visible to Customer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="flex-1 border-zinc-600 text-white hover:bg-zinc-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                >
                  {saving ? (
                    <>
                      <SpinnerGap size={16} className="mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FloppyDisk size={16} className="mr-2" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {photo?.caption ? (
                  <p className="text-white">{photo.caption}</p>
                ) : (
                  <p className="text-zinc-500 italic">No caption</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <Badge
                    className={`text-xs ${
                      photo?.visibility === "customer"
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        : "bg-zinc-700 text-zinc-400 border-zinc-600"
                    }`}
                  >
                    {photo?.visibility === "customer" ? (
                      <><Eye size={12} className="mr-1" /> Visible to Customer</>
                    ) : (
                      <><EyeSlash size={12} className="mr-1" /> Internal Only</>
                    )}
                  </Badge>
                </div>
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="border-zinc-600 text-white hover:bg-zinc-800"
                  data-testid="lightbox-edit-btn"
                >
                  <PencilSimple size={16} className="mr-1" />
                  Edit
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PhotoLightbox;
